import React, { useState } from "react";
import SelectVendors from "components/ui/selectors/SelectVendors";
import SelectCustomers from "components/ui/selectors/SelectCustomers";
import Input from "components/ui/Input";
import {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  ActionButton,
  EmptyRow,
} from "components/ui/Table";
import Modal from "components/ui/modals";
import { useFetchOverdueBills, useSendDueReminders } from "http-hooks/dueReminder/useDueReminder";
import { EyeIcon, EnvelopeIcon } from "@heroicons/react/24/outline";

const initialEmailTypes = {
  customerGeneral: true,
  customerSales: false,
  customerAccounts: true,
  customerScheme: false,
};

const DueReminder = () => {
  const { mutateAsync: fetchBills, isPending: isFetching } = useFetchOverdueBills();
  const { mutateAsync: sendReminders, isPending: isSending } = useSendDueReminders();

  const [vendor, setVendor] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [billsAfter, setBillsAfter] = useState("");
  const [daysFrom, setDaysFrom] = useState("30");
  const [result, setResult] = useState(null);
  const [viewCustomer, setViewCustomer] = useState(null);
  const [emailResult, setEmailResult] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTypes, setEmailTypes] = useState(initialEmailTypes);

  const toggleEmailType = (key) => {
    setEmailTypes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const hasAnyEmailType = Object.values(emailTypes).some(Boolean);

  const handleFetch = async (e) => {
    e.preventDefault();
    setResult(null);
    setEmailResult(null);

    try {
      const data = await fetchBills({
        vendid: vendor?.ps_Id || null,
        customers: customers.map((c) => c.ps_Id),
        billsAfter: billsAfter || null,
        daysFrom: parseInt(daysFrom) || 0,
      });
      setResult(data);
    } catch (error) {
      console.error("Failed to fetch overdue bills:", error);
    }
  };

  const handleSendReminders = async () => {
    try {
      const data = await sendReminders({
        vendid: vendor?.ps_Id || null,
        customers: customers.map((c) => c.ps_Id),
        billsAfter: billsAfter || null,
        daysFrom: parseInt(daysFrom) || 0,
        emailTypes,
      });
      setEmailResult(data);
      setShowEmailModal(false);
    } catch (error) {
      console.error("Failed to send reminders:", error);
    }
  };

  const formatAmount = (amount) => {
    return parseFloat(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-IN");
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold leading-6 text-gray-900">
            Due Reminder
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Find overdue bills and send payment reminders to customers via email
          </p>
        </div>
      </div>

      <div className="mt-8 bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
        <form onSubmit={handleFetch}>
          <div className="px-4 py-6 sm:p-8">
            <div className="grid grid-cols-[1fr_1fr_auto_auto_auto] gap-3 items-end">
              <div>
                <SelectCustomers
                  value={customers}
                  onChange={setCustomers}
                  label="Customers (optional)"
                  isMulti={true}
                  placeholder="All or select"
                />
              </div>

              <div>
                <SelectVendors
                  value={vendor}
                  onChange={setVendor}
                  label="Vendor (optional)"
                  placeholder="All vendors"
                  isClearable={true}
                />
              </div>

              <div className="w-40">
                <Input
                  type="date"
                  label="Bills After"
                  value={billsAfter}
                  onChange={(e) => setBillsAfter(e.target.value)}
                />
              </div>

              <div className="w-36">
                <Input
                  type="number"
                  label="Overdue > Days *"
                  value={daysFrom}
                  onChange={(e) => setDaysFrom(e.target.value)}
                  placeholder="30"
                  min={0}
                />
              </div>

              <button
                type="submit"
                disabled={!daysFrom || isFetching}
                className="rounded-md bg-gray-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700 disabled:opacity-50 disabled:cursor-not-allowed h-[36px]"
              >
                {isFetching ? "Fetching..." : "Fetch"}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Results */}
      {result && (
        <div className="mt-8">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
              <p className="text-sm text-gray-500">Customers</p>
              <p className="text-2xl font-semibold text-gray-900">{result.totalCustomers}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
              <p className="text-sm text-gray-500">Total Bills</p>
              <p className="text-2xl font-semibold text-gray-900">{result.totalBills}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
              <p className="text-sm text-gray-500">Total Amount</p>
              <p className="text-2xl font-semibold text-red-600">{formatAmount(result.totalAmount)}</p>
            </div>
          </div>

          {/* Send Reminders Button */}
          {result.totalCustomers > 0 && (
            <div className="flex items-center gap-4 mb-4">
              <button
                onClick={() => setShowEmailModal(true)}
                disabled={isSending}
                className="inline-flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <EnvelopeIcon className="h-4 w-4" />
                {`Send Reminders to ${result.totalCustomers} Customer(s)`}
              </button>
              {emailResult && (
                <span className="text-sm text-gray-600">
                  Sent: {emailResult.sent}, Failed: {emailResult.failed}
                </span>
              )}
            </div>
          )}

          {/* Customer Summary Table */}
          <div className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl overflow-hidden">
            <Table>
              <THead>
                <TR>
                  <TH>#</TH>
                  <TH>Customer</TH>
                  <TH>Vendors</TH>
                  <TH>Bills</TH>
                  <TH className="text-right">Total Amount</TH>
                  <TH>Email Status</TH>
                  <TH last>Action</TH>
                </TR>
              </THead>
              <TBody>
                {result.summary.length === 0 ? (
                  <EmptyRow colSpan={7} message="No overdue bills found" />
                ) : (
                  result.summary.map((cust, idx) => {
                    const emailStatus = emailResult?.results?.find(
                      (r) => r.cusid === cust.cusid
                    );
                    const vendorNames = Object.values(cust.vendors || {})
                      .map((v) => v.vendorName)
                      .filter(Boolean);
                    return (
                      <TR key={cust.cusid}>
                        <TD>{idx + 1}</TD>
                        <TD className="font-medium">{cust.customerName}</TD>
                        <TD>
                          <div className="flex flex-wrap gap-1">
                            {vendorNames.map((vn, vi) => (
                              <span
                                key={vi}
                                className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700"
                              >
                                {vn}
                              </span>
                            ))}
                          </div>
                        </TD>
                        <TD>{cust.billCount}</TD>
                        <TD className="font-medium text-red-600 text-right">{formatAmount(cust.totalAmount)}</TD>
                        <TD>
                          {emailStatus ? (
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                                emailStatus.status === "sent"
                                  ? "bg-green-50 text-green-700"
                                  : emailStatus.status === "skipped"
                                  ? "bg-yellow-50 text-yellow-700"
                                  : "bg-red-50 text-red-700"
                              }`}
                            >
                              {emailStatus.status === "sent"
                                ? "Sent"
                                : emailStatus.status === "skipped"
                                ? emailStatus.reason
                                : "Failed"}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </TD>
                        <TD last>
                          <ActionButton
                            onClick={() => setViewCustomer(cust)}
                            variant="secondary"
                            title="View Bills"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </ActionButton>
                        </TD>
                      </TR>
                    );
                  })
                )}
              </TBody>
            </Table>
          </div>
        </div>
      )}

      {/* Email Recipients Modal */}
      {showEmailModal && (
        <Modal
          open={showEmailModal}
          setOpen={setShowEmailModal}
          maxWidth="sm"
        >
          <div className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <EnvelopeIcon className="h-5 w-5 text-red-600" />
              <h3 className="text-lg font-semibold text-gray-900">
                Select Email Recipients
              </h3>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Choose which customer email addresses to send reminders to:
            </p>

            <div className="space-y-2">
              <Input
                type="checkbox"
                label="General"
                value={emailTypes.customerGeneral}
                onChange={() => toggleEmailType("customerGeneral")}
              />
              <Input
                type="checkbox"
                label="Sales & Order"
                value={emailTypes.customerSales}
                onChange={() => toggleEmailType("customerSales")}
              />
              <Input
                type="checkbox"
                label="Accounts"
                value={emailTypes.customerAccounts}
                onChange={() => toggleEmailType("customerAccounts")}
              />
              <Input
                type="checkbox"
                label="Scheme"
                value={emailTypes.customerScheme}
                onChange={() => toggleEmailType("customerScheme")}
              />
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowEmailModal(false)}
                className="rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendReminders}
                disabled={isSending || !hasAnyEmailType}
                className="inline-flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <EnvelopeIcon className="h-4 w-4" />
                {isSending ? "Sending..." : `Send to ${result?.totalCustomers || 0} Customer(s)`}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Detail Modal - vendor-wise grouped */}
      {viewCustomer && (
        <Modal
          open={!!viewCustomer}
          setOpen={(val) => !val && setViewCustomer(null)}
          maxWidth="4xl"
        >
          <div className="p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Overdue Bills - {viewCustomer.customerName}
            </h3>
            <div className="mb-4">
              <p className="text-sm text-gray-500">
                Total: <span className="font-semibold text-red-600">{formatAmount(viewCustomer.totalAmount)}</span>
                {" "}({viewCustomer.billCount} bills)
              </p>
            </div>

            {Object.entries(viewCustomer.vendors || {}).map(([vid, vendorData]) => (
              <div key={vid} className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-800">
                    Vendor: {vendorData.vendorName}
                  </h4>
                  <span className="text-sm font-medium text-red-600">
                    {formatAmount(vendorData.totalAmount)}
                  </span>
                </div>
                <Table>
                  <THead>
                    <TR>
                      <TH>#</TH>
                      <TH>Bill Date</TH>
                      <TH>Bill No.</TH>
                      <TH>Due Date</TH>
                      <TH last className="text-right">Amount</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {vendorData.bills.map((bill, idx) => (
                      <TR key={bill.salebillid}>
                        <TD>{idx + 1}</TD>
                        <TD>{formatDate(bill.salebilldate)}</TD>
                        <TD className="font-medium">{bill.salebillno}</TD>
                        <TD>{formatDate(bill.salebillduedt)}</TD>
                        <TD last className="font-medium text-red-600 text-right">
                          {formatAmount(bill.salebillvalue)}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </div>
            ))}


            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setViewCustomer(null)}
                className="rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default DueReminder;
